import React, {createContext, useContext, useMemo, useReducer} from 'react';
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import {orderBy} from 'lodash'

import {actions, initialState} from './actions';
import {reducer} from './EventTimeline.reducer';
import {getEventsTimeline} from './api';
import {getGetters} from './getters';
import {getToggles} from './toggles';
import {getSetters} from "./setters";
import {EVENTS_TYPE_MAPPER} from "../constants";

const EventTimelineContext = createContext();

const EventTimelineProvider = ({children}) => {
  const globalDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);

  const setters = getSetters(dispatch)
  const getters = getGetters(state);
  const toggles = getToggles(dispatch, globalDispatch);
  const toUsernamesFilter = (userNameList = []) => {
    return userNameList.map(userName => ({
      value: userName,
      text: userName
    }))
  }

  const callbacks = {
    getEventsTimeline: async (patientId, requestBody) => {
      toggles.setEventsTimelineList([])
      setters.setTotalSize(0)
      toggles.setIsLoading(true)
      try {
        const eventsTimelineResponse = await getEventsTimeline(patientId, requestBody)
        setters.setEventTimelineSearch(requestBody)
        const {
          totalSize = 0,
          pageSize = 0,
          pageNumber = 0,
          availableEventTypes,
          userNameList,
          eventsTimelineDtos = []
        } = eventsTimelineResponse?.data ?? {}
        toggles.setEventsTimelineList(eventsTimelineDtos)
        setters.setTotalSize(totalSize)
        toggles.setHasMore(pageSize > 0 ? pageNumber <= (totalSize / pageSize) : false)
        setters.setAvailableEventTypes(orderBy(availableEventTypes ?? [], [item => item.toLowerCase()], ['asc']).map(eventType => ({
          value: eventType,
          text: EVENTS_TYPE_MAPPER[eventType] ?? eventType
        })))
        if (Array.isArray(userNameList)) {
          dispatch({type: actions.SET_USER_NAME_LIST_FILTER, payload: toUsernamesFilter(userNameList)})
        }
        return eventsTimelineResponse
      } catch (error) {
        console.error("Failed to load event timeline:", error)
        dispatch({type: actions.SET_USER_NAME_LIST_FILTER, payload: []})
        toggles.setHasMore(false)
        return null
      } finally {
        toggles.setIsLoading(false)
      }
    },
    scrollEventsTimeline: async (patientId, requestBody) => {
      const eventTimelineSearch = {
        ...requestBody,
        pageNumber: state.eventTimelineSearch?.pageNumber + 1
      };

      toggles.setIsLoading(true)
      try {
        const eventsTimelineResponse = await getEventsTimeline(patientId, eventTimelineSearch)
        const {
          totalSize = 0,
          pageSize = 0,
          pageNumber = 0,
          eventsTimelineDtos = []
        } = eventsTimelineResponse?.data ?? {}
        toggles.setEventsTimelineList([
          ...state.eventsTimelineList,
          ...eventsTimelineDtos
        ])
        setters.setEventTimelineSearch(eventTimelineSearch)
        setters.setPageNumber(pageNumber + 1)
        setters.setTotalSize(totalSize)
        toggles.setHasMore(pageSize > 0 ? pageNumber <= (totalSize / pageSize) : false)
      } catch (error) {
        console.error("Failed to scroll event timeline:", error)
        toggles.setHasMore(false)
      } finally {
        toggles.setIsLoading(false)
      }
    },
    setUserNameListFilter: (id) => {
      return getEventsTimeline(id, {})
        .then(res => {
          dispatch({
            type: actions.SET_USER_NAME_LIST_FILTER,
            payload: toUsernamesFilter(res?.data?.userNameList ?? [])
          })
          return res
        })
        .catch(() => {
          dispatch({type: actions.SET_USER_NAME_LIST_FILTER, payload: []})
          return null
        })
    }
  }

  const value = useMemo(() => ({
    ...setters,
    ...getters,
    ...toggles,
    ...callbacks
  }), [setters, getters, toggles, callbacks])

  return (
    <EventTimelineContext.Provider value={value}>
      {children}
    </EventTimelineContext.Provider>
  )
};
const useEventTimelineContext = () => useContext(EventTimelineContext);

EventTimelineProvider.propTypes = {
  children: PropTypes.node
};

export {
  EventTimelineProvider,
  useEventTimelineContext,
}
